import { IInboundEmail } from "@/types/data-models";

/**
 * Which inbox emails can be turned into deals in one pass.
 *
 * Kept as a pure module, following `wall-entries.ts` and `file-groups.ts`, so
 * the rule can be tested without mounting the dialog.
 *
 * The eligibility decision is the server's, not ours. `auto_create_suggested`
 * is derived per request in `listInboundEmails` — the workspace flag is on, the
 * email still needs review, it has not been dismissed, the extraction finished
 * and was confident, and a company name came out of it. Re-deriving any of that
 * here would let the bulk button and the per-email banner drift apart, and they
 * are supposed to be the same offer.
 */

/** The AI writes the string "N/A" rather than omitting a field it couldn't find. */
export const extractedValue = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed && trimmed !== "N/A" ? trimmed : undefined;
};

export type AutoCreateCandidate = {
  email: IInboundEmail;
  companyName: string;
  website?: string;
};

export const candidatesFrom = (
  emails: IInboundEmail[]
): AutoCreateCandidate[] =>
  emails.reduce<AutoCreateCandidate[]>((acc, email) => {
    if (!email.auto_create_suggested) return acc;

    // The server's own rule already requires a company name, but it is computed
    // against a separate read. Belt and braces: a deal called "undefined" is a
    // worse outcome than a deal not created.
    const companyName = extractedValue(email.extracted_info?.company_name);
    if (!companyName) return acc;

    acc.push({
      email,
      companyName,
      website: extractedValue(email.extracted_info?.website),
    });
    return acc;
  }, []);
